import React, {useEffect} from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import {Controller, useFieldArray, useForm} from 'react-hook-form';

/* ───────── types ───────── */
export type LinkInput = {
  id?: number;
  customerId: number | '' | string;
  cropIds: number[];
  otherText?: string;
};
export type Submission = {links: LinkInput[]};

export const OTHER_CUSTOMER_ID = 'OTHER';

/* ───────── component ───────── */
export function FdCustomerSupplierForm(props: {
  customers: {id: number; name: string}[];
  crops: {id: number; name: string}[];
  defaultLinks: LinkInput[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (d: Submission) => void;
  onCancel: () => void;
}) {
  const {control, handleSubmit, watch, setValue, reset} = useForm<Submission>({
    defaultValues: {links: props.defaultLinks},
  });
  const {fields, append, remove, replace} = useFieldArray({
    control,
    name: 'links',
  });
  const linksWatch = watch('links');

  // Reset form when defaultLinks change (e.g., when loading "Other" entries from localStorage)
  useEffect(() => {
    if (props.defaultLinks && props.defaultLinks.length >= 0) {
      replace(props.defaultLinks);
      reset({links: props.defaultLinks}, {keepDefaultValues: false});
    }
  }, [JSON.stringify(props.defaultLinks), replace, reset]);

  return (
    <div className="generic-form-container fd-customer-form">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(props.onSubmit)}>
          <div className="form-section">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Primary customers</Typography>
              </Grid>
            </Grid>

            {fields.map((field, idx) => {
              const customerId = linksWatch?.[idx]?.customerId;
              const isOther = customerId === OTHER_CUSTOMER_ID;

              return (
                <Grid
                  container
                  spacing={2}
                  key={field.id}
                  style={{marginBottom: '16px'}}
                >
                  {/* customer */}
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name={`links.${idx}.customerId`}
                      control={control}
                      rules={{required: true}}
                      render={({field: {onChange, value, ...field}}) => (
                        <TextField
                          {...field}
                          select
                          label="Customer"
                          variant="outlined"
                          fullWidth
                          value={value || ''}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            onChange(
                              newValue === '' || newValue === OTHER_CUSTOMER_ID
                                ? newValue
                                : Number(newValue),
                            );
                            setValue(`links.${idx}.cropIds`, []); // reset crops
                            if (newValue !== OTHER_CUSTOMER_ID) {
                              setValue(`links.${idx}.otherText`, ''); // reset other text
                            }
                          }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          SelectProps={{
                            displayEmpty: true,
                          }}
                        >
                          <MenuItem value="" disabled>
                            <em>Select customer</em>
                          </MenuItem>
                          {props.customers.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name}
                            </MenuItem>
                          ))}
                          <MenuItem value={OTHER_CUSTOMER_ID}>Other</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>

                  {/* crops or other text */}
                  <Grid item xs={12} sm={6}>
                    {isOther ? (
                      <Controller
                        name={`links.${idx}.otherText`}
                        control={control}
                        rules={{
                          required: 'Please specify customer and products',
                        }}
                        render={({
                          field: {onChange, value, ...field},
                          fieldState: {error},
                        }) => (
                          <TextField
                            {...field}
                            variant="outlined"
                            label="Specify customer and products"
                            fullWidth
                            multiline
                            rows={3}
                            value={value || ''}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message || null}
                            placeholder="e.g., ABC Company - Corn, Wheat"
                          />
                        )}
                      />
                    ) : (
                      <Controller
                        name={`links.${idx}.cropIds`}
                        control={control}
                        render={({field}) => (
                          <Autocomplete
                            multiple
                            options={props.crops}
                            getOptionLabel={(o) => o.name}
                            value={props.crops.filter((c) =>
                              field.value.includes(c.id),
                            )}
                            onChange={(_, v) =>
                              field.onChange(v.map((x) => x.id))
                            }
                            disableCloseOnSelect
                            renderTags={(value, getTagProps) =>
                              value.map((opt, i) => (
                                <Chip
                                  {...getTagProps({index: i})}
                                  key={opt.id}
                                  label={opt.name}
                                  color="primary"
                                  size="small"
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Products"
                                fullWidth
                              />
                            )}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  {/* remove */}
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Button onClick={() => remove(idx)} size="small">
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              );
            })}

            {/* add button */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  onClick={() =>
                    append({customerId: '', cropIds: [], otherText: ''})
                  }
                >
                  Add
                </Button>
              </Grid>
            </Grid>
          </div>

          {/* footer actions */}
          <Grid container spacing={2} style={{marginTop: '1rem'}}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                fullWidth
                onClick={props.onCancel}
                disabled={props.isLoading}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
                disabled={props.isLoading}
              >
                {props.isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  props.submitButtonTitle
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
